/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { useCallback, useEffect, useMemo, useState,useRef } from "react";
import { API, DIALOGOPENFOR, PROVISION_CHECK_VAL } from "../../../globals/globals";
import {
  useGetLteCellsQuery,
  useGetNrCellListMutation,
  useGetNrCellsQuery,
  useGetRadioNodesQuery,
  useGetZoneCellsQuery,
  useSetDeviceConfigMutation,
  useSetRequestPartialRefreshRnMutation,
} from "../../../store/api/deviceConfig/deviceConfigApiSlice";
import { radioNodesColumns } from "../../../components/Shared/Table/Columns";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { deviceVersionSelector } from "../../../store/filters/Nodes/selectors";
import { confirmDialogSelector, paginationModelSelector } from "../../../store/helpers/helperSelectors";
import { useStoreDispatch } from "../../../store/hooks";
import { OPERATIONTYPES, constStr, mmwModelKeys } from "../../../globals/constants";
import {
  prepareRadioCuNrCellPayload,
  prepareRadioLteCellPayload,
  prepareRadioNodePayload,
  prepareRadioNodeProvisionPayload,
  prepareRadioPayload,
  prepareRemoveRadioLteCellPayload,
  prepareRemoveRadioNrCellPayload,
  prepareRnCuDuProvisionPayload,
  prepareRnDrawerZonePayload,
} from "../../../components/Configuration/helpers";
import { setBasicDialogOpenFor, setConfirmDialog, setOpenToast } from "../../../store/helpers/helperReducers";
import {
  isOpenRnCuDrawerSelector,
  isOpenRnDrawerSelector,
  radioFirstLteCellSelector,
  radioFirstNrCellSelector,
  radioNodeSelector,
  radioSecondLteCellSelector,
  radioSecondNrCellSelector,
  selectedDeviceSelector,
  selectedRnCellConfigSelector,
} from "../../../store/api/devices/selectors";
import {
  ICellListData,
  ICellsConfigData,
  ICellsCuConfigData,
  IFullDeviceDetails,
  ILteCell,
  INrCell,
  IRadioListBandData,
  IRadioNodesDataGridRow,
  ISectorProvision,
  IZoneDevice,
} from "../../../store/api/deviceConfig/types";
import { useGetRnCellConfigQuery, useGetRnLteCellConfigMutation } from "../../../store/api/devices/deviceApiSlice";
import { IBandData, IDuCellInfo, IRadioNodeCellConfig, IRnCellInfo, IRnLteCellConfig } from "../../../store/api/devices/types";
import {
  setOpenRnCuDrawer,
  setOpenRnDrawer,
  setRadioFirstLteCell,
  setRadioFirstNrCell,
  setRadioSecondLteCell,
  setRadioSecondNrCell,
  setSelectedRadioNodeRow,
  setSelectedRnCellConfig,
  setCellSaved
} from "../../../store/api/devices/reducers";
import {
  checkRadioValueToUpdateOnly,
  checkSimilarity,
  handleNewCellListData,
  handleOnlyCellUpdatedValues,
  initialDuCellInfo,
  initialRadioLteCellInfo,
  resetCellInfo,
  resetDuCellInfo,
  resetRnDrawerInfo,
} from "../helpers";
import { IOption, IProvCheck } from "../../../components/Shared/Filter/types";
import { errorMessage } from "../../../utils/helpers";
import { IKeyValueAny } from "../../../components/Administrations/types";
import { initialState } from "../../../store/helpers/helperInitialState";
import { selectProfilePermissionsSelector } from "../../../store/api/auth/selectors";
import ProfilePermissions from "../../../utils/ProfilePermissions";

export const useRadioNodesData = () => {
  const location = useLocation();
  const dispatch = useStoreDispatch();
  const locationPath = location?.pathname.split("/");
  const permissions: ProfilePermissions | null = useSelector(selectProfilePermissionsSelector);

  const [status, setStatus] = useState("");
  const [alarm, setAlarm] = useState("");
  const swVersion = useSelector(deviceVersionSelector);
  const { radioNode = initialState.pagination } = useSelector(paginationModelSelector);
  const openConfirmDialog = useSelector(confirmDialogSelector);
  const selectedDevice = useSelector(selectedDeviceSelector);
  const [setDeviceConfig] = useSetDeviceConfigMutation();
  const [selectedRnRow, setSelectedRnRow] = useState<IRadioNodesDataGridRow>();
  const [selectedRowData, setSelectedRowData] = useState<IRadioNodesDataGridRow>();

  const [dataForPartialRefresh, setDataForPartialRefresh] = useState<Array<IRadioNodesDataGridRow>>();
  const [setPartialRefresh] = useSetRequestPartialRefreshRnMutation();

  const [rowClicked, setRowClicked] = useState(false);

  const args = {
    filters: `page=${radioNode.page.toString()}&size=${radioNode.pageSize.toString()}&status=${status.toString()}&alarm=${alarm.toString()}&swVersion=${swVersion}`,
    deviceKey: locationPath[2],
  };

  const { data, isLoading, refetch } = useGetRadioNodesQuery(args, {
    pollingInterval: API.ENDPOINTS.RADIONODES.pollingInterval || API.POLLING_INTERVAL,
  });

  const defaultPageSize = {
    page: 0,
    pageSize: 100,
  };

  const nrArgs = {
    filters: `page=${defaultPageSize.page.toString()}&size=${defaultPageSize.pageSize.toString()}`,
    deviceKey: locationPath[2],
  };
  const { data: nrList } = useGetNrCellsQuery(nrArgs, {
    pollingInterval: API.ENDPOINTS.NRCELLS.pollingInterval || API.POLLING_INTERVAL,
  });

  useEffect(() => {
    if (!rowClicked) {
      dispatch(setRadioFirstLteCell(initialRadioLteCellInfo));
      dispatch(setRadioSecondLteCell(initialRadioLteCellInfo));
      dispatch(setRadioFirstNrCell(initialDuCellInfo));
      dispatch(setRadioSecondNrCell(initialDuCellInfo));
      dispatch(setOpenRnCuDrawer(false));
      dispatch(setOpenRnDrawer(false));     
    }
  }, [dispatch, rowClicked]);

  const deleteRnHandler = async (actionName: string) => {
    if (!actionName) return;
    try {
      let response: any;
      let provisionIndex: number = 0;
      let messageStr: string = "";
      if (actionName.match(constStr.yesDelete.toLowerCase())) {
        if (openConfirmDialog === DIALOGOPENFOR.RADIONODE) {
          const radioNodePayload =
            !!selectedDevice?.objectKey && prepareRadioNodePayload(selectedRnRow, selectedDevice?.objectKey, OPERATIONTYPES.DELETE, provisionIndex);
          response = await setDeviceConfig(radioNodePayload);
          messageStr = `The deletion process for Radio Node has been initiated.`;
        }

        if (response?.error) throw response.error;
        if (response?.data?.status === 200 || response?.data?.statusCodeValue === 200 || response?.data?.statusCode === "OK") {
          dispatch(
            setOpenToast({
              openToast: true,
              toastType: "success",
              message: messageStr,
            })
          );
          refetch();
        }
      }
    } catch (error: any) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  };

  const partialRefresh = useCallback(async () => {
    if (!dataForPartialRefresh || dataForPartialRefresh.length === 0) {
      return;
    }

    const radioNodePath = "InternetGatewayDevice.RadioNode";
    const partialRefreshPath: string[] = dataForPartialRefresh.flatMap((data) => [`${radioNodePath}.${data.id}.MfgData.ModelNumber`]);
    if (partialRefreshPath.length > 0) {
      await setPartialRefresh({ deviceKey: selectedDevice.objectKey, dataPathList: partialRefreshPath });
    }
  }, [dataForPartialRefresh, selectedDevice.objectKey, setPartialRefresh]);


  const partialRefreshFullRn = useCallback(async () => {
   
    const radioNodePath = "InternetGatewayDevice.RadioNode.";
    const radioNodePaths: string[] = [radioNodePath];
   
    await setPartialRefresh({ deviceKey: selectedDevice.objectKey, dataPathList: radioNodePaths });

  }, [selectedDevice.objectKey, setPartialRefresh]);

  useEffect(() => {
    setTimeout(() => {
      partialRefresh();
    }, 5000);
  }, [partialRefresh]);

  useEffect(() => {
    refetch();
  }, [refetch])

  return {
    permissions,
    data,
    isLoading,
    radioNodesColumns,
    setStatus,
    setAlarm,
    deleteRnHandler,
    setSelectedRnRow,
    rowClicked,
    setRowClicked,
    selectedRowData,
    setSelectedRowData,
    setDataForPartialRefresh,
    nrList,
    partialRefresh,
    partialRefreshFullRn
  };
};

interface IPayload {
  [key: string]: string;
}

export const useRadioNodeDrawer = (rnModelKey?: string, isCellDrawer?: string) => {
  const dispatch = useStoreDispatch();
  const location = useLocation();
  const locationPath = location?.pathname.split("/");
  const permissions: ProfilePermissions | null = useSelector(selectProfilePermissionsSelector);
  const { lteCell = initialState.pagination } = useSelector(paginationModelSelector);
  const deviceDetail: IFullDeviceDetails = useSelector(selectedDeviceSelector);
  const selectedRnCellConfigData: IRadioNodeCellConfig = useSelector(selectedRnCellConfigSelector);
  const radioNode: IRadioNodesDataGridRow = useSelector(radioNodeSelector);
  const radioFirstLteCellDetails: IRnLteCellConfig = useSelector(radioFirstLteCellSelector);
  const radioSecondLteCellDetails: IRnLteCellConfig = useSelector(radioSecondLteCellSelector);
  const [isCreateCellRadioInfoOne, setIsCreateCellRadioInfoOne] = useState(false);
  const [isCreateCellRadioInfoTwo, setIsCreateCellRadioInfoTwo] = useState(false);
  const [selectedRadioNode, setSelectedRadioNode] = useState<IRadioNodeCellConfig>(selectedRnCellConfigData);
  const { cellId: commonCellIdFirst } = radioFirstLteCellDetails?.lteCellConfig?.lte?.lteRan?.lteRanCommon || {};
  const { cellId: commonCellIdSecond } = radioSecondLteCellDetails?.lteCellConfig?.lte?.lteRan?.lteRanCommon || {};
  const [zoneOption, setZoneOption] = useState<IOption[]>([]);
  const [zones, setZones] = useState<IOption[]>([]);
  const [initialZones, setInitialZones] = useState<IOption[]>([]);
  const [zoneOption2, setZoneOption2] = useState<IOption[]>([]);
  const [zones2, setZones2] = useState<IOption[]>([]);
  const [initialZones2, setInitialZones2] = useState<IOption[]>([]);
  const swVersion = useSelector(deviceVersionSelector);
  const isOpenRnDrawer = useSelector(isOpenRnDrawerSelector);
  const [selectedBand1, setSelectedBand1] = useState(Array.isArray(radioNode?.radios) ? radioNode?.radios[0]?.band : "");
  const [selectedBand2, setSelectedBand2] = useState(Array.isArray(radioNode?.radios) ? radioNode?.radios[1]?.band : "");
  const { phyCellIDInUse: cellPciFirst, referenceSignalPowerInUse: cellPowerFirst } =
    radioFirstLteCellDetails?.lteCellConfig?.lte?.lteRan?.lteRanRf || {};
  const { phyCellIDInUse: cellPciSecond, referenceSignalPowerInUse: cellPowerSecond } =
    radioSecondLteCellDetails?.lteCellConfig?.lte?.lteRan?.lteRanRf || {};
  const [selectedRadio, setSelectedRadio] = useState("radio1");
  const [isAddEditRadioOne, setIsAddEditRadioOne] = useState("add");
  const [isAddEditRadioTwo, setIsAddEditRadioTwo] = useState("add");
  const [errMsg, setErrMsg] = useState<IPayload>({});
  const [errMsg2, setErrMsg2] = useState<IPayload>({});
  const [isLoadingConfigStatus, setIsLoadingConfigStatus] = useState<boolean>(false);
  const [isLoadingCellInfo, setIsLoadingCellInfo] = useState<boolean>(true);
  const [provisionValid, setProvisionValid] = useState<IProvCheck>({
    isValid: false,
    provMsg: "",
  });
  const openConfirmDialog = useSelector(confirmDialogSelector);
  const isProvisioningRef = useRef(false);

  const initialCellOne = {
    cellId: radioFirstLteCellDetails?.index || 0,
    cellName: radioFirstLteCellDetails?.name || "",
    cellPci: cellPciFirst || 0,
    cellDescription: radioFirstLteCellDetails?.description || "",
    power: cellPowerFirst || 0,
    commonCellId: commonCellIdFirst || "",
    actualOta: "",
    zone: "",
    band: "",
  };
  const initialCellTwo = {
    cellId: radioSecondLteCellDetails?.index || 0,
    cellName: radioSecondLteCellDetails?.name || "",
    cellPci: cellPciSecond || 0,
    cellDescription: radioSecondLteCellDetails?.description || "",
    power: cellPowerSecond || 0,
    commonCellId: commonCellIdSecond || "",
    actualOta: "",
    zone: "",
    band: "",
  };
  const [radioNodeFormData, setRadioNodeFormData] = useState<IRadioNodesDataGridRow>({
    id: 0,
    name: "",
    description: "",
    status: "",
    enable: false,
    ethernetId: "",
    ipAddress: "",
    model: "",
    nodeType: "",
    location: "",
    latitude: "",
    longitude: "",
    radios: [],
  });
  const [cellFormDataOne, setCellFormDataOne] = useState<IRnCellInfo>({
    cellId: radioFirstLteCellDetails?.index || 0,
    cellName: radioFirstLteCellDetails?.name || "",
    cellPci: cellPciFirst || 0,
    cellDescription: radioFirstLteCellDetails?.description || "",
    power: cellPowerFirst || 0,
    commonCellId: commonCellIdFirst || "",
    actualOta: "",
    zone: "",
    band: "",
    status: "",
  });
  const [cellFormDataTwo, setCellFormDataTwo] = useState<IRnCellInfo>({
    cellId: radioSecondLteCellDetails?.index || 0,
    cellName: radioSecondLteCellDetails?.name || "",
    cellPci: cellPciSecond || 0,
    cellDescription: radioSecondLteCellDetails?.description || "",
    power: cellPowerSecond || 0,
    commonCellId: commonCellIdSecond || "",
    actualOta: "",
    zone: "",
    band: "",
    status: "",
  });
  const [provisionIndex, setProvisionIndex] = useState<number>(0);
  const [errProvision, setErrProvision] = useState<IPayload>({});
  const [radioNodeList, setRadioNodeList] = useState<Array<IRadioNodesDataGridRow>>([]);
  const [selectedBandVal, setSelectedBandVal] = useState("");
  const [radioTypeCheck, setRadioTypeCheck] = useState("");

  const rnArgs = {
    modelKey: rnModelKey || "",
  };

  const lteArgs = {
    filters: `page=${lteCell.page.toString()}&size=${lteCell.pageSize.toString()}&swVersion=${swVersion}`,
    deviceKey: locationPath[2],
  };

  const { data: lteCellList } = useGetLteCellsQuery(lteArgs, {
    pollingInterval: API.ENDPOINTS.LTECELLS.pollingInterval || API.POLLING_INTERVAL,
  });

  const [setDeviceConfig, configStatus] = useSetDeviceConfigMutation();
  const [setRnCellConfig] = useGetRnLteCellConfigMutation();

  const zoneDefaultPageSize = {
    page: 0,
    pageSize: 100,
  };

  const args = {
    filters: `page=${zoneDefaultPageSize.page.toString()}&size=${zoneDefaultPageSize.pageSize.toString()}`,
    deviceKey: locationPath[2],
    deviceNode: deviceDetail?.nodeType,
  };
  const { data: zoneList } = useGetZoneCellsQuery(args, {
    pollingInterval: API.ENDPOINTS.LTEZONECELLS.pollingInterval || API.POLLING_INTERVAL,
  });

  const { data } = useGetRnCellConfigQuery(rnArgs);

  const handleMinMaxBandwiths = (selectedBand: any, element: IZoneDevice, selectedRadioNode: IRadioNodeCellConfig) => {
    let isValidRange = false;
    let newBandData: IBandData[] = [];
    newBandData = selectedRadioNode?.bandData?.filter((el: IBandData) => el?.bandValue?.toLowerCase() === selectedBand?.toLowerCase());
    if (newBandData?.length > 0) {
      if (
        Number(element?.eARFCNDL) >= newBandData[0]?.minDLARFCN &&
        Number(element?.eARFCNDL) <= newBandData[0]?.maxDLARFCN &&
        Number(element?.eARFCNUL) >= newBandData[0]?.minULARFCN &&
        Number(element?.eARFCNDL) <= newBandData[0]?.maxULARFCN
      ) {
        isValidRange = true;
      }
    }
    return isValidRange;
  };

  useEffect(() => {
    if (zoneList?.data?.length > 0) {
      let formData =
        selectedRadio === "radio1"
          ? radioFirstLteCellDetails?.index
          : selectedRadio === "radio2"
          ? radioSecondLteCellDetails?.index
          : radioFirstLteCellDetails?.index;
      if (formData) {
        getZoneIdCellList(formData?.toString() || "", zoneList?.data);
      }
    }
  }, [zoneList?.data, radioFirstLteCellDetails?.index, radioSecondLteCellDetails?.index, selectedRadio]);

  const getZoneIdCellList = (target: string, data: IZoneDevice[]) => {
    if (target && data?.length > 0) {
      const filteredResult = data.filter((item) => {
        const cellList = item?.cellList
          ?.replace(/[^\d\s]/g, "")
          .trim()
          .split(" ");
        return cellList?.includes(target);
      });
      if (filteredResult?.length > 0) {
        const arr = filteredResult?.map((item) => {
          return {
            title: item.index || "",
            value: item.index || "",
          };
        });
        if (selectedRadio === "radio1") {
          setInitialZones(arr || []);
          setZones(arr || []);
        } else if (selectedRadio === "radio2") {
          setInitialZones2(arr || []);
          setZones2(arr || []);
        }
      }
    }
  };

  const getRnCellDetails = async () => {
    if (Array.isArray(radioNode?.radios) && radioNode?.radios?.length > 0) {
      let cellId = selectedRadio === "radio1" ? radioNode?.radios[0]?.cellId : radioNode?.radios[1]?.cellId;
      if (cellId !== null) {
        let cellArgs = {
          deviceKey: locationPath[2],
          radioNodeId: Number(cellId),
        };
        try {
          const response: any = await setRnCellConfig(cellArgs);
          if (response?.error) throw response?.error;
          let resPayload = {
            cellId: response?.data?.index || 0,
            cellName: response?.data?.name || "",
            cellPci: response?.data?.lteCellConfig?.lte?.lteRan?.lteRanRf?.phyCellIDInUse || 0,
            cellDescription: response?.data?.description || "",
            power: response?.data?.lteCellConfig?.lte?.lteRan?.lteRanRf?.referenceSignalPowerInUse || 0,
            commonCellId: response?.data?.lteCellConfig?.lte?.lteRan?.lteRanCommon?.cellId || "",
            status: response?.data?.operState || "",
          };
          if (selectedRadio === "radio1") {
            dispatch(setRadioFirstLteCell(response?.data));
            setCellFormDataOne({ ...cellFormDataOne, ...resPayload });
          } else {
            dispatch(setRadioSecondLteCell(response?.data));
            setCellFormDataTwo({ ...cellFormDataTwo, ...resPayload });
          }
        } catch (error: any) {
          dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
        }
      }
    }
  };

  useEffect(() => {
    if (selectedRnCellConfigData?.rnCellData?.radios > 0 && selectedRadio) {
      if (Array.isArray(radioNode?.radios)) {
        if (selectedRadio === "radio1" && radioNode?.radios[0]?.cellId) {
          getRnCellDetails();
        }
        if (selectedRadio === "radio2" && radioNode?.radios[1]?.cellId) {
          getRnCellDetails();
        }
      }
    }
    // Dependency purposefully excluded to avoid endless loop condition coming due to (getRnCellDetails, radioNode?.radios)
    // The above functionality only calls when selectedRadio changes to fetch different radio Cell configurations and render on drawer.
  }, [selectedRadio, selectedRnCellConfigData?.rnCellData?.radios]);

  useEffect(() => {
    if (zoneList?.data?.length > 0) {
      let zoneIndexList: IOption[] = [];
      const zoneData = zoneList?.data || [];
      let selectedBand = selectedRadio === "radio1" ? selectedBand1 : selectedRadio === "radio2" ? selectedBand2 : "";
      if (zoneData.length > 0) {
        zoneData.forEach((element: IZoneDevice) => {
          if (!!selectedBand && selectedBand !== "not-available" && handleMinMaxBandwiths(selectedBand, element, selectedRadioNode)) {
            zoneIndexList.push({ title: element?.index || "", value: element?.index || "" });
          }
        });
        if (selectedRadio === "radio1") {
          setZoneOption(zoneIndexList);
        } else if (selectedRadio === "radio2") {
          setZoneOption2(zoneIndexList);
        } else return;
      }
    }
    // Dependency purposefully excluded to avoid endless loop condition coming due to ('handleMinMaxBandwiths', 'selectedBand1', and 'selectedBand2'.)
  }, [selectedBand1, selectedBand2, selectedRadio, selectedRadioNode, zoneList?.data]);

  useEffect(() => {
    if (!isOpenRnDrawer) {
      setCellFormDataOne(resetCellInfo);
      setCellFormDataTwo(resetCellInfo);
      dispatch(setSelectedRadioNodeRow(resetRnDrawerInfo));
    }
  }, [dispatch, isOpenRnDrawer]);

  useEffect(() => {
    if (!!data) {
      dispatch(setSelectedRnCellConfig(data));
      setSelectedRadioNode(data);
      getRnCellDetails();
      if (Array.isArray(radioNode?.radios)) {
        if (radioNode?.radios[0]?.index) {
          if (radioNode?.radios[0]?.cellId) {
            setIsAddEditRadioOne("edit");
            setIsCreateCellRadioInfoOne(true);
          } else {
            setIsAddEditRadioOne("add");
          }
        }
        if (radioNode?.radios[1]?.index === "2") {
          if (radioNode?.radios[1]?.cellId) {
            setIsAddEditRadioTwo("edit");
            setIsCreateCellRadioInfoTwo(true);
          } else {
            setIsAddEditRadioTwo("add");
          }
        }
      }
      setIsLoadingCellInfo(false);
    }
    // Dependency purposefully excluded to avoid endless loop condition coming due to (getRnCellDetails, radioNode?.radios)
  }, [data, dispatch]);

  useEffect(() => {
    if (isCellDrawer?.toLowerCase() === "snltecell" && !rnModelKey) {
      setIsLoadingCellInfo(false);
    }
  }, [isCellDrawer, rnModelKey]);

  const updateCellItems = (formValues: any, initialValues: any, radioFormData: any, radioNode: any) => {
    const updateFields: IKeyValueAny = {};
    if (radioFormData["latitude"] !== radioNode["latitude"]) {
      updateFields["latitude"] = radioFormData["latitude"];
      updateFields["oldLatitude"] = radioNode["latitude"];
    }
    if (radioFormData["longitude"] !== radioNode["longitude"]) {
      updateFields["longitude"] = radioFormData["longitude"];
      updateFields["oldLongitude"] = radioNode["longitude"];
    }
    if(!isProvisioningRef.current)
    {
        if (radioFormData["description"] !== radioNode["description"]) {
          updateFields["description"] = radioFormData["description"];
          updateFields["oldDescription"] = radioNode["description"];
        }
        if (radioFormData["name"] !== radioNode["name"]) {
          updateFields["name"] = radioFormData["name"];
          updateFields["oldName"] = radioNode["name"];
        }
    }
    for (const key in formValues) {
      if (key !== "status" && formValues.hasOwnProperty(key) && formValues[key] !== initialValues[key]) {
        updateFields[key] = formValues[key];
      }
    }
    return updateFields;
  };

  const handleCreateCell = (radioType: string) => {
    let res = "both";
    if (radioType === "radio1") {
      if (Array.isArray(radioNode?.radios) && radioNode?.radios[0].band?.toLowerCase() !== "not-available" && radioNode?.radios?.length > 0) {
        if (radioNode.radios[0].index === "1" && radioNode.radios[0].cellId === null) {
          res = "cell";
        }
      }
    } else {
      if (Array.isArray(radioNode?.radios) && radioNode?.radios[0].band?.toLowerCase() !== "not-available" && radioNode?.radios?.length === 2) {
        if (radioNode.radios[1].index === "2" && radioNode.radios[1].cellId === null) {
          res = "cell";
        }
      }
    }
    return res;
  };

  const checkMmwStatus = () => {
    let isMmwType: boolean = false;
    if (deviceDetail?.nodeType === "CU") {
      if (radioNodeFormData?.model && mmwModelKeys.some((item) => item?.toLowerCase() === radioNodeFormData?.model?.toLowerCase())) {
        isMmwType = true;
      }
    }
    return isMmwType;
  };

  const validateProvision = (provisionIndex: number, rnCellConfig: IRadioNodeCellConfig) => {
    let obj = {
      isValid: false,
      provMsg: "",
      actionType: "",
    };
    if (!!provisionIndex) {
      const validArr = handleValidate(true);
      if (!Object.values(validArr[0]).some((err) => err !== "") && !Object.values(validArr[1]).some((err) => err !== "")) {
        obj["isValid"] = true;
        obj["provMsg"] = "";
      } else {
        obj["isValid"] = false;
        obj["provMsg"] = "Please update the fields where validations failed!";
      }
    } else {
      obj["isValid"] = false;
      obj["provMsg"] = "Provide Provision Index value to Provision Radionode.";
      obj["actionType"] = "";
    }
    return obj;
  };

  const handleSubmitProvisionData = () => {
    // handle validation for all fields andd then submit data if verifies all the fields for radio and cell
    if (!Object.values(errMsg).some((err) => err !== "") && !Object.values(errMsg2).some((err) => err !== "")) {
      const { isValid, provMsg } = validateProvision(provisionIndex, selectedRnCellConfigData);
      if (radioNodeFormData?.id >= PROVISION_CHECK_VAL) {
        setProvisionValid({ ...provisionValid, isValid: isValid, provMsg: provMsg });
        if (provMsg && provMsg?.length > 0) {
          return dispatch(setConfirmDialog(DIALOGOPENFOR.PROVISIONRN));
        }
        !!provisionIndex && updateProvisioning();
      } else return;
    }
  };

  const onBlurHandler = (event: any) => {
    const { name, value } = event?.target;

    let lteIndexArray = lteCellList?.data?.map((el: ILteCell) => el?.index);
    if (selectedRadio === "radio1") {
      if (name === "cellId") {
        if (value < 1) {
          setErrMsg({ [name]: `Should be greater than 0` });
          return;
        } else if (lteIndexArray?.includes(value)) {
          setErrMsg({ [name]: `This cellId already exist. Try another!!` });
          return;
        } else if (value === cellFormDataTwo?.cellId) {
          setErrMsg({ [name]: `This cellId already added in radio2. Try another!!` });
          return;
        } else {
          setErrMsg({ [name]: `` });
          setErrMsg2({ [name]: `` });
        }
      } else if (name === "cellPci") {
        if (value === cellFormDataOne?.cellId) setErrMsg({ [name]: `` });
      } else if (name === "commonCellId") {
        if (value === cellFormDataOne?.commonCellId) setErrMsg({ [name]: `` });
      }
    } else {
      if (name === "cellId") {
        if (value < 1) {
          setErrMsg({ [name]: `Should be greater than 0` });
          return;
        } else if (lteIndexArray?.includes(value)) {
          setErrMsg2({ [name]: `This cellId already exist. Try another!!` });
          return;
        } else if (value === cellFormDataOne?.cellId) {
          setErrMsg2({ [name]: `This cellId already added in radio1. Try another!!` });
          return;
        } else {
          setErrMsg({ [name]: `` });
          setErrMsg2({ [name]: `` });
          return;
        }
      } else if (name === "cellPci") {
        if (value === cellFormDataTwo?.cellId) setErrMsg2({ [name]: `` });
      } else if (name === "commonCellId") {
        if (value === cellFormDataTwo?.commonCellId) setErrMsg2({ [name]: `` });
      }
    }
  };

  const handleValidate = (isProvision: boolean) => {
    let errorMsg1: any = {};
    let errorMsg2: any = {};
    if (!isProvision && selectedRadio === "radio1" && !cellFormDataOne?.cellId) {
      errorMsg1.cellId = `Cell ID is required!`;
    }
    if (!isProvision && selectedRadio === "radio2" && !cellFormDataTwo?.cellId) {
      errorMsg2.cellId = `Cell ID is required!`;
    }
    if (cellFormDataOne?.cellId) {
      if (!cellFormDataOne?.cellPci) {
        errorMsg1.cellPci = `Cell PCI is required!`;
      }
      if (Number(cellFormDataOne?.cellId) !== Number(cellFormDataOne?.commonCellId)) {
        errorMsg1.commonCellId = `Common Cell ID should be same as Cell ID.`;
      }
    }
    if (cellFormDataTwo?.cellId) {
      if (!cellFormDataTwo?.cellPci) {
        errorMsg2.cellPci = `Cell PCI is required!`;
      }
      if (Number(cellFormDataTwo?.cellId) !== Number(cellFormDataTwo?.commonCellId)) {
        errorMsg2.commonCellId = `Common Cell ID should be same as Cell ID.`;
      }
    }
    if (Object?.keys(errorMsg1?.length > 0)) {
      setErrMsg({ ...errMsg, ...errorMsg1 });
    }
    if (Object?.keys(errorMsg2?.length > 0)) {
      setErrMsg2({ ...errMsg, ...errorMsg2 });
    }
    return [errorMsg1, errorMsg2];
  };

  const onBlurProvision = (event: any) => {
    const { name, value } = event?.target;
    if (name === "provisionVal") {
      if (value < 1) {
        setErrMsg({ [name]: `Should be greater than 0` });
        return;
      } else if (value >= PROVISION_CHECK_VAL) {
        setErrMsg({ [name]: `Try less than ${PROVISION_CHECK_VAL}` });
        return;
      } else if (radioNodeList?.some((el) => Number(el?.id) === Number(value))) {
        setErrMsg({ [name]: `This already exists. Try another!!` });
        return;
      } else {
        setErrMsg({ [name]: `` });
        return;
      }
    }
  };

  const getOperationType = (oldBand: string, newBand: string, radioIndex: number) => {
    if (oldBand === undefined) {
      return {
        radio: radioIndex + 1,
        oldBandData: oldBand,
        newBandData: newBand,
        action: OPERATIONTYPES.CREATE,
      };
    } else
      return {
        radio: radioIndex + 1,
        oldBandData: oldBand,
        newBandData: newBand,
        action: OPERATIONTYPES.UPDATE,
      };
  };

  const handleCreateFlatRadio = (radioNodeData: IRadioNodesDataGridRow, band1: string, band2: string) => {
    const oldBandFirst = radioNodeData?.radios?.[0]?.band;
    const newBandFirst = band1;
    const oldBandSecond = radioNodeData?.radios?.[1]?.band;
    const newBandSecond = band2;

    let arr = [];

    if (selectedRadioNode?.rnCellData?.radios > 0) {
      let radiosCount = selectedRadioNode?.rnCellData?.radios;
      for (let i = 0; i < radiosCount; i++) {
        if (i === 0 && oldBandFirst !== newBandFirst) {
          arr.push(getOperationType(oldBandFirst, newBandFirst, i) || "");
        }
        if (i === 1 && oldBandSecond !== newBandSecond) {
          arr.push(getOperationType(oldBandSecond, newBandSecond, i) || "");
        }
      }
    }
    return arr;
  };

  const updateProvisioning = async () => {
    setIsLoadingConfigStatus(true);   
    if (provisionIndex) {
      try {
        let response: any;
        let messageStr: string = "";
        isProvisioningRef.current = true;

        const radioAddUpdate: IRadioListBandData[] = handleCreateFlatRadio(radioNodeFormData, selectedBand1, selectedBand2);
        const cellAddUpdate: ICellsConfigData = handleProvisionCell();
        const radioNodePayload =
          !!deviceDetail?.objectKey &&
          prepareRadioNodeProvisionPayload(
            radioNodeFormData,
            deviceDetail?.objectKey,
            OPERATIONTYPES.CREATE,
            provisionIndex,
            radioAddUpdate,
            cellAddUpdate,
            deviceDetail?.nodeType
          );
        const cellListToUpdate1 = handleNewCellListData(cellFormDataOne, zones, initialZones, zoneList?.data);
        const cellListToUpdate2 = handleNewCellListData(cellFormDataTwo, zones2, initialZones2, zoneList?.data);
        response = await setDeviceConfig(radioNodePayload);
        messageStr = `The Provision process for Radio Node has been initiated.`;

        if (response?.error) throw response.error;
        if (response?.data?.status === 200 || response?.data?.statusCodeValue === 200 || response?.data?.statusCode === "OK") {
          dispatch(
            setOpenToast({
              openToast: true,
              toastType: "success",
              message: messageStr,
            })
          );
          if (cellListToUpdate1?.length > 0) {
            handleSubmitZoneConfigs(cellListToUpdate1);
          }
          if (cellListToUpdate2?.length > 0) {
            handleSubmitZoneConfigs(cellListToUpdate2);
          } else {
            dispatch(setBasicDialogOpenFor(undefined));
            dispatch(setOpenRnDrawer(false));
            setIsLoadingConfigStatus(false);
          }
        }
        isProvisioningRef.current = false;
      } catch (error: any) {
         isProvisioningRef.current = false;
        setIsLoadingConfigStatus(false);
        dispatch(setBasicDialogOpenFor(undefined));
        dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
      }
    }
  };

  const handleProvisionCell = () => {  
    const initialCellData = selectedRadio === "radio1" ? initialCellOne : initialCellTwo;
    const isMmWType = checkMmwStatus();   
   
    const newFormData1 = updateCellItems(cellFormDataOne, initialCellData, radioNodeFormData, radioNode);
    const newFormData2 = updateCellItems(cellFormDataTwo, initialCellData, radioNodeFormData, radioNode);
    let cellListToAdd = [];
    if (Object?.keys(newFormData1)?.length > 0) {
      cellListToAdd.push({
        cellData: cellFormDataOne,
        radioIndex: 1,
      });
    }
    if (Object?.keys(newFormData2)?.length > 0) {
      cellListToAdd.push({
        cellData: cellFormDataTwo,
        radioIndex: 2,
      });
    }
    isProvisioningRef.current = false ;
    return {
      isMmWType: isMmWType,
      cellLists: cellListToAdd || [],
    };
  };

  const updateRnCellInfo = async () => {
    setIsLoadingConfigStatus(true);
    try {
      let response: any;
      let messageStr: string = "";
      if (selectedBandVal) {
        const operationType = radioTypeCheck === "Add" ? OPERATIONTYPES?.CREATE : OPERATIONTYPES.UPDATE;
        const oldBandData = (selectedRadio === "radio1" ? radioNodeFormData?.radios[0]?.band : radioNodeFormData?.radios[1]?.band) || "";
        const radioNodePayload =
          !!deviceDetail?.objectKey &&
          prepareRadioPayload(radioNodeFormData, oldBandData, deviceDetail?.objectKey, selectedRadio, operationType, selectedBandVal);
        response = await setDeviceConfig(radioNodePayload);
        messageStr = `The Provision process for Radio Node has been initiated.`;
      }

      if (response?.error) throw response.error;
      if (response?.data?.status === 200 || response?.data?.statusCodeValue === 200 || response?.data?.statusCode === "OK") {
        dispatch(
          setOpenToast({
            openToast: true,
            toastType: "success",
            message: messageStr,
          })
        );
        dispatch(setBasicDialogOpenFor(undefined));
        dispatch(setOpenRnDrawer(false));
        setIsLoadingConfigStatus(false);
      }
    } catch (error: any) {
      setIsLoadingConfigStatus(false);
      dispatch(setBasicDialogOpenFor(undefined));
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  };

  const handleSubmitZoneConfigs = async (cellListToUpdate: ICellListData[]) => {
    if (cellListToUpdate?.length > 0) {
      try {
        const zonesApiRequests = cellListToUpdate?.map((el: ICellListData) => {
          return (
            el?.oldCellList !== el?.newCellList &&
            !!deviceDetail?.objectKey &&
            setDeviceConfig(prepareRnDrawerZonePayload(el, deviceDetail?.objectKey, deviceDetail?.nodeType, OPERATIONTYPES.UPDATE))
          );
        });
        let result: any;
        if (zonesApiRequests?.length > 0) {
          result = await Promise.all(zonesApiRequests);
          if (result?.error || configStatus?.isError) throw result?.error;
          if (result?.some((res: any) => res?.data?.status === 200)) {
            dispatch(
              setOpenToast({
                openToast: true,
                toastType: "success",
                message: `The data has been submited for Zone`,
              })
            );
            if (openConfirmDialog === DIALOGOPENFOR.REMOVESNZONE) {
              handleDeleteRadioCell();
            } else {
              dispatch(setBasicDialogOpenFor(undefined));
              setIsLoadingConfigStatus(false);
              dispatch(setOpenRnDrawer(false));
            }
          }
        }
      } catch (error: any) {
        setIsLoadingConfigStatus(false);
        dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
      }
    } else {
      setIsLoadingConfigStatus(false);
      dispatch(setOpenRnDrawer(false));
    }
  };

  const handleRemoveAllCellsToZones = async () => {
    setIsLoadingConfigStatus(true);
    const formData = selectedRadio === "radio1" ? cellFormDataOne : cellFormDataTwo;
    const initialZoneVal = selectedRadio === "radio1" ? initialZones : initialZones2;
    const zoneVal = selectedRadio === "radio1" ? zones : zones2;
    const isRemove = true;
    const cellListToUpdate1 = handleNewCellListData(formData, zoneVal, initialZoneVal, zoneList?.data, isRemove);
    if (cellListToUpdate1?.length > 0) {
      handleSubmitZoneConfigs(cellListToUpdate1);
    } else return;
  };

  const handleSubmitRadioNode = async (type: string) => {
    let validArr = [];
    const formData = selectedRadio === "radio1" ? cellFormDataOne : cellFormDataTwo;
    const initialCellData = selectedRadio === "radio1" ? initialCellOne : initialCellTwo;
    const cellListKeys = handleOnlyCellUpdatedValues(formData, initialCellData);
    if (cellListKeys?.length > 0) {
      validArr = handleValidate(false) || [];
    } else validArr = handleValidate(true) || [];
    if (Array?.isArray(validArr) && !Object.values(validArr[0]).some((err) => err !== "") && !Object.values(validArr[1]).some((err) => err !== "")) {
      const operationType = type === "add" ? OPERATIONTYPES?.CREATE : OPERATIONTYPES.UPDATE;
      const selectedBandValue = selectedRadio === "radio1" ? selectedBand1 : selectedBand2;
      const onlyRadioInfoUpt = checkRadioValueToUpdateOnly(formData, initialCellData);
      const isCreateRadioCell = handleCreateCell(selectedRadio);
      const isMmWType = checkMmwStatus();
      const zoneVal = selectedRadio === "radio1" ? zones : zones2;
      const initialZoneVal = selectedRadio === "radio1" ? initialZones : initialZones2;
      const cellListToUpdate1 = handleNewCellListData(formData, zoneVal, initialZoneVal, zoneList?.data);
      const newFormData = updateCellItems(formData, initialCellData, radioNodeFormData, radioNode);
      dispatch(setCellSaved(true));
      if (Object.keys(newFormData)?.length > 0) {
        setIsLoadingConfigStatus(true);
        const radioNodePayload = prepareRadioLteCellPayload(
          radioNodeFormData,
          formData,
          initialCellData,
          deviceDetail?.objectKey,
          operationType,
          selectedRadio,
          selectedBandValue,
          isCreateRadioCell,
          newFormData,
          onlyRadioInfoUpt,
          isMmWType
        );
        try {
          let result: any;
          result = await setDeviceConfig(radioNodePayload);
          if (result?.error || configStatus?.isError) throw result?.error;
          if (result.data?.status === 200) {
            dispatch(
              setOpenToast({
                openToast: true,
                toastType: "success",
                message: `The data has been submited`,
              })
            );
            handleSubmitZoneConfigs(cellListToUpdate1);
          }
        } catch (error: any) {
          setIsLoadingConfigStatus(false);
          dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
          dispatch(setCellSaved(false));
        }
      } else if (!checkSimilarity(initialZoneVal, zoneVal)) {
        setIsLoadingConfigStatus(true);
        handleSubmitZoneConfigs(cellListToUpdate1);
      }
    }
  };

  const handleDeleteRadioCell = async () => {
    setIsLoadingConfigStatus(true);
    const operationType = OPERATIONTYPES.DELETE;
    const formData = selectedRadio === "radio1" ? cellFormDataOne : cellFormDataTwo;
    const radioNodePayload = prepareRemoveRadioLteCellPayload(radioNodeFormData, formData, deviceDetail?.objectKey, operationType, selectedRadio);
    try {
      let result: any;

      result = await setDeviceConfig(radioNodePayload);
      if (result?.error || configStatus?.isError) throw result?.error;
      if (result.data?.status === 200) {
        dispatch(
          setOpenToast({
            openToast: true,
            toastType: "success",
            message: `The data has been submited`,
          })
        );
        setIsLoadingConfigStatus(false);
        dispatch(setOpenRnDrawer(false));
        dispatch(setConfirmDialog(undefined));
        if(selectedRadio === "radio1") {
          dispatch(setRadioFirstLteCell(initialRadioLteCellInfo));
        }
        if(selectedRadio === "radio2") {
          dispatch(setRadioSecondLteCell(initialRadioLteCellInfo));
        } 
      }
    } catch (error: any) {
      setIsLoadingConfigStatus(false);
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  };

  return {
    permissions,
    zones,
    setZones,
    zoneOption,
    setZoneOption,
    selectedRadioNode,
    setSelectedRadioNode,
    cellFormDataOne,
    setCellFormDataOne,
    cellFormDataTwo,
    setCellFormDataTwo,
    isCreateCellRadioInfoOne,
    setIsCreateCellRadioInfoOne,
    isCreateCellRadioInfoTwo,
    setIsCreateCellRadioInfoTwo,
    selectedBand1,
    setSelectedBand1,
    selectedBand2,
    setSelectedBand2,
    radioNodeFormData,
    setRadioNodeFormData,
    handleSubmitRadioNode,
    selectedRadio,
    setSelectedRadio,
    isAddEditRadioOne,
    setIsAddEditRadioOne,
    isAddEditRadioTwo,
    setIsAddEditRadioTwo,
    initialCellOne,
    initialCellTwo,
    handleDeleteRadioCell,
    onBlurHandler,
    errMsg,
    setErrMsg,
    errMsg2,
    setErrMsg2,
    isLoadingConfigStatus,
    onBlurProvision,
    updateProvisioning,
    provisionIndex,
    setProvisionIndex,
    errProvision,
    setErrProvision,
    setRadioNodeList,
    isLoadingCellInfo,
    selectedBandVal,
    setSelectedBandVal,
    updateRnCellInfo,
    radioTypeCheck,
    setRadioTypeCheck,
    setZoneOption2,
    zoneOption2,
    setZones2,
    zones2,
    initialZones2,
    initialZones,
    setInitialZones2,
    provisionValid,
    handleSubmitProvisionData,
    handleRemoveAllCellsToZones,
    deviceDetail,
  };
};

export const useRNCuAppDrawer = (rnModelKey: string, isCellDrawer?: string) => {
  const dispatch = useStoreDispatch();
  const location = useLocation();
  const locationPath = location?.pathname.split("/");
  const permissions: ProfilePermissions | null = useSelector(selectProfilePermissionsSelector);
  const deviceDetail: IFullDeviceDetails = useSelector(selectedDeviceSelector);
  const selectedRnCellConfigData = useSelector(selectedRnCellConfigSelector);
  const radioNode: IRadioNodesDataGridRow = useSelector(radioNodeSelector);
  const radioFirstNrCellDetails: INrCell = useSelector(radioFirstNrCellSelector);
  const radioSecondNrCellDetails: INrCell = useSelector(radioSecondNrCellSelector);
  const openConfirmDialog = useSelector(confirmDialogSelector);
  const [isCreateCellRadioInfoOne, setIsCreateCellRadioInfoOne] = useState(false);
  const [isCreateCellRadioInfoTwo, setIsCreateCellRadioInfoTwo] = useState(false);
  const [selectedRadioNode, setSelectedRadioNode] = useState<IRadioNodeCellConfig>(selectedRnCellConfigData);
  const [zoneOption, setZoneOption] = useState<IOption[]>([]);
  const [zones, setZones] = useState<IOption[]>([]);
  const [initialZones, setInitialZones] = useState<IOption[]>([]);
  const [selectedBand, setSelectedBand] = useState(Array.isArray(radioNode?.radios) ? radioNode?.radios[0]?.band : "");
  const {
    phyCellIDInUse: cellPciFirst,
    referenceSignalPowerInUse: cellPowerFirst,
    txPowerPercentage: txPowerFirst,
    pbchPowerInUse: pbchPowerOne,
  } = radioFirstNrCellDetails?.nrcellConfig?.ng?.ngran?.ngranRf || {};
  const {
    phyCellIDInUse: cellPciSecond,
    referenceSignalPowerInUse: cellPowerSecond,
    txPowerPercentage: txPowerSecond,
    pbchPowerInUse: pbchPowerTwo,
  } = radioSecondNrCellDetails?.nrcellConfig?.ng?.ngran?.ngranRf || {};
  const { cellId: commonCellIdFirst } = radioFirstNrCellDetails?.nrcellConfig?.ng?.ngran?.ngranCommon || {};
  const { cellId: commonCellIdSecond } = radioSecondNrCellDetails?.nrcellConfig?.ng?.ngran?.ngranCommon || {};
  const [selectedRadio, setSelectedRadio] = useState("radio1");
  const [isAddEditRadioOne, setIsAddEditRadioOne] = useState("add");
  const [isAddEditRadioTwo, setIsAddEditRadioTwo] = useState("add");
  const [nrCellListData, setNrCellListData] = useState<string[]>([]);
  const [nrCellRowsData, setNrCellRowsData] = useState<INrCell[]>([]);
  const [errMsg, setErrMsg] = useState<IPayload>({});
  const [errMsg2, setErrMsg2] = useState<IPayload>({});
  const [isLoadingConfigStatus, setIsLoadingConfigStatus] = useState<boolean>(false);
  const [isLoadingCellInfo, setIsLoadingCellInfo] = useState<boolean>(true);
  const [duBandData, setDuBandData] = useState("");
  const initialCellOne = {
    cellId: radioFirstNrCellDetails?.index || "",
    cellName: radioFirstNrCellDetails?.name || "",
    cellPci: cellPciFirst || "",
    cellDescription: radioFirstNrCellDetails?.description || "",
    txPower: txPowerFirst || "",
    commonCellId: commonCellIdFirst || "",
    power: cellPowerFirst || "",
    pbchPower: pbchPowerOne || "",
    zone: "",
    status: "",
  };
  const initialCellTwo = {
    cellId: radioSecondNrCellDetails?.index || "",
    cellName: radioSecondNrCellDetails?.name || "",
    cellPci: cellPciSecond || "",
    cellDescription: radioSecondNrCellDetails?.description || "",
    txPower: txPowerSecond || "",
    commonCellId: commonCellIdSecond || "",
    power: cellPowerSecond || "",
    pbchPower: pbchPowerTwo || "",
    zone: "",
    status: "",
  };
  const [radioNodeFormData, setRadioNodeFormData] = useState<IRadioNodesDataGridRow>({
    id: 0,
    name: "",
    description: "",
    status: "",
    enable: false,
    ethernetId: "",
    ipAddress: "",
    model: "",
    nodeType: "",
    radios: [],
    location: "",
    latitude: "",
    longitude: "",
  });
  const [cellFormDataOne, setCellFormDataOne] = useState<IDuCellInfo>({
    cellId: radioFirstNrCellDetails?.index || "",
    cellName: radioFirstNrCellDetails?.name || "",
    cellPci: cellPciFirst || "",
    cellDescription: radioFirstNrCellDetails?.description || "",
    txPower: txPowerFirst || "",
    commonCellId: commonCellIdFirst || "",
    power: cellPowerFirst || "",
    pbchPower: pbchPowerOne || "",
    zone: "",
    status: "",
  });
  const [cellFormDataTwo, setCellFormDataTwo] = useState<IDuCellInfo>({
    cellId: radioSecondNrCellDetails?.index || "",
    cellName: radioSecondNrCellDetails?.name || "",
    cellPci: cellPciSecond || "",
    cellDescription: radioSecondNrCellDetails?.description || "",
    txPower: txPowerSecond || "",
    commonCellId: commonCellIdSecond || "",
    power: cellPowerSecond || "",
    pbchPower: pbchPowerTwo || "",
    zone: "",
    status: "",
  });
  const [selectedBandVal, setSelectedBandVal] = useState("");
  const [radioTypeCheck, setRadioTypeCheck] = useState("");

  const isOpenRnCuDrawer = useSelector(isOpenRnCuDrawerSelector);

  const [sectorHubs, setSectorHubs] = useState<any>([]);
  const [provisionIndex, setProvisionIndex] = useState<number>(0);
  const [errProvision, setErrProvision] = useState<IPayload>({});
  const [radioNodeList, setRadioNodeList] = useState<Array<IRadioNodesDataGridRow>>([]);
  const [sectorFormData, setSectorFormData] = useState<ISectorProvision>({
    name: "",
    ethernetId: "",
    index: 1,
    radioNodeIndex: 0,
  });
  const [provisionValid, setProvisionValid] = useState<IProvCheck>({
    isValid: false,
    provMsg: "",
  });

  const rnArgs = {
    modelKey: rnModelKey || "",
  };

  const { data } = useGetRnCellConfigQuery(rnArgs);

  const [getNrCellList] = useGetNrCellListMutation();
  const [setDeviceConfig, configStatus] = useSetDeviceConfigMutation();

  const defaultPageSize = {
    page: 0,
    pageSize: 100,
  };

  const args = {
    filters: `page=${defaultPageSize.page.toString()}&size=${defaultPageSize.pageSize.toString()}`,
    deviceKey: locationPath[2],
    deviceNode: deviceDetail?.nodeType,
  };

  const nrArgs = {
    filters: `page=${defaultPageSize.page.toString()}&size=${defaultPageSize.pageSize.toString()}`,
    deviceKey: locationPath[2],
  };

  const { data: zoneList } = useGetZoneCellsQuery(args, {
    pollingInterval: API.ENDPOINTS.LTEZONECELLS.pollingInterval || API.POLLING_INTERVAL,
  });

  const nrBandData: any = useMemo(() => {
    return {
      "nr-band-260": {
        minArfcn: 2229166,
        maxArfcn: 2279165,
        bw: 100,
      },
      "nr-band-261": {
        minArfcn: 2070833,
        maxArfcn: 2084999,
        bw: 100,
      },
    };
  }, []);

  const checkArfcnRange = (zoneObj: IZoneDevice, bandConfigs: any) => {
    const eARFCNDL = Number(zoneObj?.eARFCNDL);
    const eARFCNUL = Number(zoneObj?.eARFCNUL);
    const minArfcn = Number(bandConfigs?.minArfcn);
    const maxArfcn = Number(bandConfigs?.maxArfcn);

    return eARFCNUL >= minArfcn && eARFCNUL <= maxArfcn && eARFCNDL >= minArfcn && eARFCNDL <= maxArfcn;
  };

  const handleMinMaxBandwiths = useCallback(
    (selectedBand: string, element: IZoneDevice) => {
      let isValidRange = false;
      const nrBandIndex = Object?.keys(nrBandData)?.filter((el) => el === selectedBand);
      if (nrBandIndex?.length > 0) {
        let bandConfigs = nrBandData[`${nrBandIndex[0]}`];
        const result = checkArfcnRange(element, bandConfigs);
        if (result) {
          isValidRange = true;
        }
      }
      return isValidRange;
    },
    [nrBandData]
  );

  useEffect(() => {
    if (zoneList?.data?.length > 0) {
      let zoneIndexList: IOption[] = [];
      const zoneData = zoneList?.data || [];
      if (zoneData.length > 0) {
        zoneData.forEach((element: IZoneDevice) => {
          if (handleMinMaxBandwiths(selectedBand, element)) {
            zoneIndexList.push({ title: element?.index || "", value: element?.index || "" });
          }
        });
      }
      setZoneOption(zoneIndexList);
    }
  }, [handleMinMaxBandwiths, selectedBand, zoneList?.data]);

  useEffect(() => {
    if (zoneList?.data?.length > 0) {
      let formData =
        selectedRadio === "radio1"
          ? radioFirstNrCellDetails?.index
          : selectedRadio === "radio2"
          ? radioSecondNrCellDetails?.index
          : radioFirstNrCellDetails?.index;
      if (formData) {
        getZoneIdCellList(formData?.toString() || "", zoneList?.data);
      }
    }
  }, [zoneList?.data, selectedRadio, radioFirstNrCellDetails?.index, radioSecondNrCellDetails?.index]);

  const getZoneIdCellList = (target: string, data: IZoneDevice[]) => {
    if (target && data?.length > 0) {
      const filteredResult = data.filter((item) => {
        const cellList = item?.cellList
          ?.replace(/[^\d\s]/g, "")
          .trim()
          .split(" ");
        return cellList?.includes(target);
      });
      if (filteredResult?.length > 0) {
        const arr = filteredResult?.map((item) => {
          return {
            title: item.index || "",
            value: item.index || "",
          };
        });
        setInitialZones(arr || []);
        setZones(arr || []);
      }
    }
  };

  useEffect(() => {
    const fetchNrCellListData = async () => {
      try {
        const result: any = await getNrCellList(nrArgs);
        if (result?.error) throw result?.error?.message;
        if (result.data?.data?.length > 0) {
          setNrCellListData(() => result?.data?.data?.map((el: INrCell) => el?.index));
          setNrCellRowsData(result?.data?.data);
          let cellObj: any = {};
          cellObj = result?.data?.data?.find((el: INrCell) => Number(el?.radioNode) === radioNode?.id);
          if (!!cellObj?.index) {
            dispatch(setRadioFirstNrCell(cellObj));
            let resPayload = {
              cellId: cellObj?.index || "",
              cellName: cellObj?.name || "",
              cellPci: cellObj?.nrcellConfig?.ng?.ngran?.ngranRf?.phyCellIDInUse || "",
              cellDescription: cellObj?.description || "",
              power: cellObj?.nrcellConfig?.ng?.ngran?.ngranRf?.referenceSignalPowerInUse || "",
              txPower: cellObj?.nrcellConfig?.ng?.ngran?.ngranRf?.txPowerPercentage || "",
              pbchPower: cellObj?.nrcellConfig?.ng?.ngran?.ngranRf?.pbchPowerInUse || "",
              commonCellId: cellObj?.nrcellConfig?.ng?.ngran?.ngranCommon?.cellId || "",
              status: cellObj?.operState || "",
            };
            setIsAddEditRadioOne("edit");
            setIsCreateCellRadioInfoOne(true);
            setCellFormDataOne({ ...cellFormDataOne, ...resPayload });
            setIsLoadingCellInfo(false);
          } else setIsLoadingCellInfo(false);
        } else setIsLoadingCellInfo(false);
      } catch (error) {
        setIsLoadingCellInfo(false);
        dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
      }
    };

    // if(radioNode?.model === "") {
    //   dispatch(setSelectedRnCellConfig(resetRnCellInfo));
    //   setSelectedRadioNode(resetRnCellInfo);
    //   return;
    // }
    if (data) {
      setSelectedRadioNode(data);
      dispatch(setSelectedRnCellConfig(data));
      fetchNrCellListData();
    }
    // Dependency purposefully excluded to avoid endless loop condition coming due to adding other dependencies
  }, [data, dispatch, radioNode.model]);

  useEffect(() => {
    if (!isOpenRnCuDrawer) {
      setCellFormDataOne(resetDuCellInfo);
      setCellFormDataTwo(resetDuCellInfo);
      dispatch(setSelectedRadioNodeRow(resetRnDrawerInfo));
    }
  }, [dispatch, isOpenRnCuDrawer]);

  // useEffect(() => {
  //   if (isOpenRnCuDrawer) {
  //     const handleBandMmwType = () => {
  //       if (deviceDetail?.nodeType === "RCP" || deviceDetail?.nodeType === "CU") {
  //         if (rnModelKey && mmwModelKeys.some((item) => item?.toLowerCase() === rnModelKey?.toLowerCase())) {
  //           // if (rnModelKey?.toLowerCase() === "scrn-530-39") {
  //           //   setSelectedBand("nr-band-260");
  //           // } else setSelectedBand("nr-band-261");
  //         }
  //       }
  //     };

  //     handleBandMmwType();
  //   }
  // }, [deviceDetail?.nodeType, isOpenRnCuDrawer, rnModelKey]);

  useEffect(() => {
    if (isCellDrawer?.toLowerCase() === "cunrcell" && !rnModelKey) {
      setIsLoadingCellInfo(false);
    }
  }, [isCellDrawer, rnModelKey]);

  const handleCreateCell = (radioType: string) => {
    let res = "both";
    if (radioType === "radio1") {
      const { radios } = radioNodeFormData;
      if (Array.isArray(radios) && radios?.length > 0 && radios[0].index) {
        res = "cell";
      }
    }
    return res;
  };

  const onBlurHandler = (event: any) => {
    if (selectedRadio === "radio1") {
      const { name, value } = event?.target;
      if (name === "cellId") {
        if (value < 1) {
          setErrMsg({ [name]: `Should be greater than 0` });
          return;
        } else if (nrCellListData?.includes(value)) {
          setErrMsg({ [name]: `This cellId already exist. Try another!!` });
          return;
        } else {
          setErrMsg({ [name]: `` });
        }
      } else if (name === "cellPci") {
        if (value === cellFormDataOne?.cellId) setErrMsg({ [name]: `` });
      } else if (name === "commonCellId") {
        if (value === cellFormDataOne?.commonCellId) setErrMsg({ [name]: `` });
      }
    } else {
      const { name, value } = event?.target;
      if (name === "cellId") {
        if (value < 1) {
          setErrMsg({ [name]: `Should be greater than 0` });
          return;
        } else if (nrCellListData?.includes(value)) {
          setErrMsg2({ [name]: `This cellId already exist. Try another!!` });
          return;
        } else {
          setErrMsg({ [name]: `` });
          return;
        }
      } else if (name === "cellPci") {
        if (value === cellFormDataTwo?.cellId) setErrMsg2({ [name]: `` });
      } else if (name === "commonCellId") {
        if (value === cellFormDataTwo?.commonCellId) setErrMsg2({ [name]: `` });
      }
    }
  };

  const onBlurProvision = (event: any) => {
    const { name, value } = event?.target;
    if (name === "provisionVal") {
      if (value < 1) {
        setErrMsg({ [name]: `Should be greater than 0` });
        return;
      } else if (value >= PROVISION_CHECK_VAL) {
        setErrMsg({ [name]: `Try less than ${PROVISION_CHECK_VAL}` });
        return;
      } else if (radioNodeList?.some((el) => Number(el?.id) === Number(value))) {
        setErrMsg({ [name]: `This already exists. Try another!!` });
        return;
      } else {
        setErrMsg({ [name]: `` });
        return;
      }
    }
  };

  const validateProvision = (provisionIndex: number, rnCellConfig: IRadioNodeCellConfig) => {
    let obj = {
      isValid: false,
      provMsg: "",
      actionType: "",
    };
    if (!!provisionIndex) {
      const validArr = handleValidate(true);
      if (!Object.values(validArr[0]).some((err) => err !== "") && !Object.values(validArr[1]).some((err) => err !== "")) {
        obj["isValid"] = true;
        obj["provMsg"] = "";
      } else {
        obj["isValid"] = false;
        obj["provMsg"] = "Please update the fields where validations failed!";
      }
    } else {
      obj["isValid"] = false;
      obj["provMsg"] = "Provide Provision Index value to Provision Radionode.";
      obj["actionType"] = "";
    }
    return obj;
  };

  const handleSubmitProvisionData = () => {
    // handle validation for all fields andd then submit data if verifies all the fields for radio and cell
    if (!Object.values(errMsg).some((err) => err !== "") && !Object.values(errMsg2).some((err) => err !== "")) {
      const { isValid, provMsg } = validateProvision(provisionIndex, selectedRnCellConfigData);
      if (radioNodeFormData?.id >= PROVISION_CHECK_VAL) {
        setProvisionValid({ ...provisionValid, isValid: isValid, provMsg: provMsg });
        if (provMsg && provMsg?.length > 0) {
          return dispatch(setConfirmDialog(DIALOGOPENFOR.RNCUPROVISION));
        }
        !!provisionIndex && updateProvisioning();
      } else return;
    }
  };

  const updateCellItems = (formValues: any, initialValues: any, radioFormData: any, radioNode: any) => {
    const updateFields: IKeyValueAny = {};
    if (radioFormData["description"] !== radioNode["description"]) {
      updateFields["description"] = radioFormData["description"];
      updateFields["oldDescription"] = radioNode["description"];
    }
    if (radioFormData["name"] !== radioNode["name"]) {
      updateFields["name"] = radioFormData["name"];
      updateFields["oldName"] = radioNode["name"];
    }
    for (const key in formValues) {
      if (key !== "status" && formValues.hasOwnProperty(key) && formValues[key] !== initialValues[key]) {
        updateFields[key] = formValues[key];
      }
    }
    return updateFields;
  };

  const updateRnCellInfo = async () => {
    setIsLoadingConfigStatus(true);
    try {
      let response: any;
      let messageStr: string = "";
      if (selectedBandVal) {
        const operationType = radioTypeCheck === "Add" ? OPERATIONTYPES?.CREATE : OPERATIONTYPES.UPDATE;
        const oldBandData = (selectedRadio === "radio1" ? radioNodeFormData?.radios[0]?.band : radioNodeFormData?.radios[1]?.band) || "";
        const radioNodePayload =
          !!deviceDetail?.objectKey &&
          prepareRadioPayload(radioNodeFormData, oldBandData, deviceDetail?.objectKey, selectedRadio, operationType, selectedBandVal);
        response = await setDeviceConfig(radioNodePayload);
        messageStr = `The Provision process for Radio Node has been initiated.`;
      }

      if (response?.error) throw response.error;
      if (response?.data?.status === 200 || response?.data?.statusCodeValue === 200 || response?.data?.statusCode === "OK") {
        dispatch(
          setOpenToast({
            openToast: true,
            toastType: "success",
            message: messageStr,
          })
        );
        dispatch(setBasicDialogOpenFor(undefined));
        dispatch(setOpenRnCuDrawer(false));
        setIsLoadingConfigStatus(false);
      }
    } catch (error: any) {
      setIsLoadingConfigStatus(false);
      dispatch(setBasicDialogOpenFor(undefined));
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  };

  const getOperationType = (oldBand: string, newBand: string, radioIndex: number) => {
    if (oldBand === undefined) {
      return {
        radio: radioIndex + 1,
        oldBandData: oldBand,
        newBandData: newBand,
        action: OPERATIONTYPES.CREATE,
      };
    } else
      return {
        radio: radioIndex + 1,
        oldBandData: oldBand,
        newBandData: newBand,
        action: OPERATIONTYPES.UPDATE,
      };
  };

  const handleCreateFlatRadio = (radioNodeData: IRadioNodesDataGridRow, band1?: string, band2?: string) => {
    const oldBandFirst = radioNodeData?.radios?.[0]?.band || "";
    const newBandFirst = band1 || "";
    const oldBandSecond = radioNodeData?.radios?.[1]?.band || "";
    const newBandSecond = band2 || "";

    let arr = [];

    if (selectedRadioNode?.rnCellData?.radios > 0) {
      let radiosCount = selectedRadioNode?.rnCellData?.radios;
      for (let i = 0; i < radiosCount; i++) {
        if (i === 0 && oldBandFirst !== newBandFirst) {
          arr.push(getOperationType(oldBandFirst, newBandFirst, i) || "");
        }
        if (i === 1 && oldBandSecond !== newBandSecond) {
          arr.push(getOperationType(oldBandSecond, newBandSecond, i) || "");
        }
      }
    }
    return arr;
  };

  const handleRemoveAllCellsToZones = async () => {
    setIsLoadingConfigStatus(true);
    const formData = selectedRadio === "radio1" ? cellFormDataOne : cellFormDataTwo;
    const isRemove = true;
    const cellListToUpdate1 = handleNewCellListData(formData, zones, initialZones, zoneList?.data, isRemove);
    if (cellListToUpdate1?.length > 0) {
      handleSubmitZoneConfigs(cellListToUpdate1);
    } else return;
  };

  const handleProvisionCell = () => {
    const initialCellData = selectedRadio === "radio1" ? initialCellOne : initialCellTwo;
    const newFormData1 = updateCellItems(cellFormDataOne, initialCellData, radioNodeFormData, radioNode);
    let cellListToAdd = [];
    if (Object?.keys(newFormData1)?.length > 0) {
      cellListToAdd.push({
        cellData: cellFormDataOne,
        radioIndex: 1,
      });
    }
    return {
      isMmWType: true,
      cellLists: cellListToAdd || [],
    };
  };

  const updateProvisioning = async () => {
    setIsLoadingConfigStatus(true);
    if (provisionIndex) {
      try {
        let response: any;
        let messageStr: string = "";

        const radioAddUpdate: IRadioListBandData[] = handleCreateFlatRadio(radioNodeFormData, selectedBand);
        const cellAddUpdate: ICellsCuConfigData = handleProvisionCell();
        const radioNodePayload =
          !!deviceDetail?.objectKey &&
          prepareRnCuDuProvisionPayload(
            radioNodeFormData,
            deviceDetail?.objectKey,
            OPERATIONTYPES.CREATE,
            provisionIndex,
            radioAddUpdate,
            cellAddUpdate,
            deviceDetail?.swVersion
          );
        const cellListToUpdate1 = handleNewCellListData(cellFormDataOne, zones, initialZones, zoneList?.data);
        response = await setDeviceConfig(radioNodePayload);
        messageStr = `The Provision process for Radio Node has been initiated.`;

        if (response?.error) throw response.error;
        if (response?.data?.status === 200 || response?.data?.statusCodeValue === 200 || response?.data?.statusCode === "OK") {
          dispatch(
            setOpenToast({
              openToast: true,
              toastType: "success",
              message: messageStr,
            })
          );
          if (cellListToUpdate1?.length > 0) {
            handleSubmitZoneConfigs(cellListToUpdate1);
          } else {
            dispatch(setBasicDialogOpenFor(undefined));
            dispatch(setOpenRnCuDrawer(false));
            setIsLoadingConfigStatus(false);
          }
        }
      } catch (error: any) {
        setIsLoadingConfigStatus(false);
        dispatch(setBasicDialogOpenFor(undefined));
        dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
      }
    }
  };

  const handleSubmitZoneConfigs = async (cellListToUpdate: ICellListData[]) => {
    if (cellListToUpdate?.length > 0) {
      try {
        const zonesApiRequests = cellListToUpdate?.map((el: ICellListData) => {
          return (
            el?.oldCellList !== el?.newCellList &&
            !!deviceDetail?.objectKey &&
            setDeviceConfig(prepareRnDrawerZonePayload(el, deviceDetail?.objectKey, deviceDetail?.nodeType, OPERATIONTYPES.UPDATE))
          );
        });
        let result: any;
        if (zonesApiRequests?.length > 0) {
          result = await Promise.all(zonesApiRequests);
          if (result?.error || configStatus?.isError) throw result?.error;
          if (result?.some((res: any) => res?.data?.status === 200)) {
            dispatch(
              setOpenToast({
                openToast: true,
                toastType: "success",
                message: `The data has been submited for Zone`,
              })
            );
            if (openConfirmDialog === DIALOGOPENFOR.REMOVECUZONE) {
              handleDeleteRadioCell();
            } else {
              dispatch(setBasicDialogOpenFor(undefined));
              setIsLoadingConfigStatus(false);
              dispatch(setOpenRnCuDrawer(false));
            }
          }
        }
      } catch (error: any) {
        setIsLoadingConfigStatus(false);
        dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
      }
    } else {
      setIsLoadingConfigStatus(false);
      dispatch(setOpenRnCuDrawer(false));
    }
  };

  const handleValidate = (isProvision: boolean) => {
    let errorMsg1: any = {};
    let errorMsg2: any = {};
    if (!isProvision && selectedRadio === "radio1" && !cellFormDataOne?.cellId) {
      errorMsg1.cellId = `Cell ID is required!`;
    }
    if (!isProvision && selectedRadio === "radio2" && !cellFormDataTwo?.cellId) {
      errorMsg2.cellId = `Cell ID is required!`;
    }
    if (cellFormDataOne?.cellId) {
      if (!cellFormDataOne?.cellPci) {
        errorMsg1.cellPci = `Cell PCI is required!`;
      }
      if (Number(cellFormDataOne?.cellId) !== Number(cellFormDataOne?.commonCellId)) {
        errorMsg1.commonCellId = `Common Cell ID should be same as Cell ID.`;
      }
    }
    if (cellFormDataTwo?.cellId) {
      if (!cellFormDataTwo?.cellPci) {
        errorMsg2.cellPci = `Cell PCI is required!`;
      }
      if (Number(cellFormDataTwo?.cellId) !== Number(cellFormDataTwo?.commonCellId)) {
        errorMsg2.commonCellId = `Common Cell ID should be same as Cell ID.`;
      }
    }
    if (Object?.keys(errorMsg1?.length > 0)) {
      setErrMsg({ ...errMsg, ...errorMsg1 });
    }
    if (Object?.keys(errorMsg2?.length > 0)) {
      setErrMsg2({ ...errMsg, ...errorMsg2 });
    }
    return [errorMsg1, errorMsg2];
  };

  const handleSubmitRadioNode = async (type: string) => {
    let validArr = [];
    const formData = selectedRadio === "radio1" ? cellFormDataOne : cellFormDataTwo;
    const initialCellData = selectedRadio === "radio1" ? initialCellOne : initialCellTwo;
    const cellListKeys = handleOnlyCellUpdatedValues(formData, initialCellData);
    if (cellListKeys?.length > 0) {
      validArr = handleValidate(false) || [];
    } else validArr = handleValidate(true) || [];
    if (Array?.isArray(validArr) && !Object.values(validArr[0]).some((err) => err !== "") && !Object.values(validArr[1]).some((err) => err !== "")) {
      setIsLoadingConfigStatus(true);
      const operationType = type === "add" ? OPERATIONTYPES?.CREATE : OPERATIONTYPES.UPDATE;
      const isCreateRadioCell = handleCreateCell(selectedRadio);
      const onlyRadioInfoUpt = checkRadioValueToUpdateOnly(formData, initialCellData);
      const cellListToUpdate = handleNewCellListData(formData, zones, initialZones, zoneList?.data);
      const newFormData = updateCellItems(formData, initialCellData, radioNodeFormData, radioNode);
      if (Object.keys(newFormData)?.length > 0) {
        const radioNodePayload = prepareRadioCuNrCellPayload(
          radioNodeFormData,
          formData,
          initialCellData,
          deviceDetail?.objectKey,
          operationType,
          selectedRadio,
          isCreateRadioCell,
          newFormData,
          onlyRadioInfoUpt,
          deviceDetail?.swVersion
        );
        try {
          let result: any;
          result = await setDeviceConfig(radioNodePayload);
          if (result?.error || configStatus?.isError) throw result?.error;
          if (result.data?.status === 200) {
            dispatch(
              setOpenToast({
                openToast: true,
                toastType: "success",
                message: `The data has been submited`,
              })
            );
            handleSubmitZoneConfigs(cellListToUpdate);
          }
        } catch (error: any) {
          setIsLoadingConfigStatus(false);
          dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
        }
      } else if (!checkSimilarity(initialZones, zones)) {
        setIsLoadingConfigStatus(true);
        handleSubmitZoneConfigs(cellListToUpdate);
      }
    }
  };

  const handleDeleteRadioCell = async () => {
    setIsLoadingConfigStatus(true);
    const operationType = OPERATIONTYPES.DELETE;
    const formData = selectedRadio === "radio1" ? cellFormDataOne : cellFormDataTwo;
    const radioNodePayload = prepareRemoveRadioNrCellPayload(radioNodeFormData, formData, deviceDetail?.objectKey, operationType, selectedRadio);
    try {
      let result: any;

      result = await setDeviceConfig(radioNodePayload);
      if (result?.error || configStatus?.isError) throw result?.error;
      if (result.data?.status === 200) {
        dispatch(
          setOpenToast({
            openToast: true,
            toastType: "success",
            message: `The data has been submited`,
          })
        );
        setIsLoadingConfigStatus(false);
        dispatch(setOpenRnCuDrawer(false));
        dispatch(setConfirmDialog(undefined));
        if(selectedRadio === "radio1") {
          dispatch(setRadioFirstNrCell(initialDuCellInfo));
        }
        if(selectedRadio === "radio2") {
          dispatch(setRadioSecondNrCell(initialDuCellInfo));
        } 
      }
    } catch (error: any) {
      setIsLoadingConfigStatus(false);
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  };

  return {
    permissions,
    zones,
    setZones,
    zoneOption,
    setZoneOption,
    selectedRadioNode,
    setSelectedRadioNode,
    cellFormDataOne,
    setCellFormDataOne,
    cellFormDataTwo,
    setCellFormDataTwo,
    isCreateCellRadioInfoOne,
    setIsCreateCellRadioInfoOne,
    isCreateCellRadioInfoTwo,
    setIsCreateCellRadioInfoTwo,
    selectedBand,
    setSelectedBand,
    radioNodeFormData,
    setRadioNodeFormData,
    handleSubmitRadioNode,
    selectedRadio,
    setSelectedRadio,
    isAddEditRadioOne,
    setIsAddEditRadioOne,
    isAddEditRadioTwo,
    setIsAddEditRadioTwo,
    initialCellOne,
    initialCellTwo,
    handleDeleteRadioCell,
    onBlurHandler,
    errMsg,
    setErrMsg,
    errMsg2,
    setErrMsg2,
    isLoadingConfigStatus,
    sectorHubs,
    setSectorHubs,
    duBandData,
    setDuBandData,
    onBlurProvision,
    updateProvisioning,
    provisionIndex,
    setProvisionIndex,
    errProvision,
    setErrProvision,
    setRadioNodeList,
    nrCellRowsData,
    sectorFormData,
    setSectorFormData,
    isLoadingCellInfo,
    selectedBandVal,
    setSelectedBandVal,
    updateRnCellInfo,
    radioTypeCheck,
    setRadioTypeCheck,
    initialZones,
    provisionValid,
    handleSubmitProvisionData,
    handleRemoveAllCellsToZones,
    deviceDetail,
  };
};
